<template>
	<div class="height-100 editor-container" ref="editor_container">
		<AddNewStepHeader
			:showMiniApps="showMiniApps"
			:searchString.sync="searchString"
			:showExpandedMenu="showExpandedMenu"
			@hide-full-menu="hideFullMenu"
			@on-arrow-up="onArrowUp"
			@on-arrow-down="onArrowDown"
			@clear-focus="focusedItemUUID = null"
		/>
		<div class="pb-7">
			<template v-if="!showMiniApps">
				<div v-for="action in ACTION_TYPES_LIST" :key="`${action.actionType}`">
					<AppSelectSubMenu
						v-if="!showExpandedMenu || showExpandedMenu === action.actionType"
						:actionType="action.actionType"
						:focusedItemUUID="focusedItemUUID"
						:showAllItems="shouldShowAllItems(action)"
						:allNodesServiceSlugs="allNodesServiceSlugs"
						:contextualNodeServiceId="contextualNodeServiceId"
						:title="appTypesItems[action.actionType].displayText"
						:items="filterItems(appTypesItems[action.actionType].apps)"
						:numOfElementsToDisplay="getNumOfElementsToDisplayInPreview()"
						:doesContextualNodeHaveContextualActions="doesContextualNodeHaveContextualActions"
						@scroll-to-element="scrollToElement"
						@show-all="onSeeAll"
						@create-code-helper-clicked="onCreateCodeHelperClicked"
						@app-selected="selectApp"
					/>
				</div>
				<ActionsMenu
					v-if="searchString && searchString.length > 2 && !showExpandedMenu"
					v-on="$listeners"
					noHeader
					title="Actions"
					showAppNamePreview
					:items="actionsFromSearchWithoutUnChangeableActions"
					:focusedItemUUID="focusedItemUUID"
					@action-selected="onActionSelect"
					@scroll-to-element="scrollToElement"
					:nodeId="editNode.id"
				/>
				<Loader small v-if="isLoadingActions" />
			</template>
			<template v-else>
				<AppSelectSubMenu
					showAllItems
					title="Mini Apps"
					:items="filterItems(miniApps)"
					:focusedItemUUID="focusedItemUUID"
					:numOfElementsToDisplay="filterItems(miniApps).length"
					@scroll-to-element="scrollToElement"
					@app-selected="selectApp"
				/>
			</template>
			<div v-if="isNoResults(filterItems(appTypesItems.all))" class="no-results ml-6 mt-2">No results</div>
		</div>
	</div>
</template>

<script>
const runJsActionKey = 'run_javascript';
import debounce from 'lodash/debounce';
import { mapGetters } from 'vuex';

import { ServiceSlugs, ServiceType } from '@ply-io/ply-front-shared-utilities';

import {
	INTERFACE,
	MAX_NUM_OF_APPS_TO_DISPLAY_IN_PREVIEW,
	POPULAR_APPS_SLUGS,
	UN_CHANGEABLE_TO_AND_FROM_SERVICE_SLUGS,
} from './BlockEditorAppSelect.const';
import BlockEditorAppSelectService from './block-editor-app-select-service';
import AddNewStepHeader from '../AddNewStep/AddNewStepHeader';
import AppSelectSubMenu from '../AddNewStep/AppSelectSubMenu';
import ActionsMenu from '../IntegrationEditor/ActionsMenu';
import ActionsMenuService from '../IntegrationEditor/actions-menu-service';
import api from '@/api';
import { CreateNodeShortcutCommand } from '@/react_app/components';
import { getActionsOfApp } from '@/store/modules/apps/actions';
import {
	getServiceAllActions,
	getAllServiceActionCollections,
	getService,
	getServiceActionCollection,
	getAllActiveServicesList,
	getAllServicesList,
	getServiceBySlug,
	getServiceActionCollectionBySlug,
	getServiceActionsByVersion,
} from '@/store/modules/apps/getters';
import { isAIMiniAppSupportOn, isLoomOn } from '@/store/modules/featureFlags/getters';
import { getPlacement } from '@/store/modules/placements/getters';
import { clearEditNode, setParamInEditNode } from '@/store/modules/selectedNode/actions';
import { editNode } from '@/store/modules/selectedNode/getters';
import { nodesDataByUUID, element, editWorkflow } from '@/store/modules/selectedWorkflow/getters';
import { removeTempBlock, setShortcutCommand } from '@/store/modules/treeBlocks/actions';
import { isEndRunNodePossible, useShortcutCommand } from '@/store/modules/treeBlocks/getters';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import AppActionService from '@/utils/AppActionService.js';
import AppService from '@/utils/AppService';
import WorkflowService from '@/utils/WorkflowService';
import ErrorHandlingService, { NodeCreationError, NodeUpdateError } from '@/utils/error-handling-service';
const ACTION_TYPES_LIST = [
	{ actionType: ServiceType.Context, showAllItems: true },
	{ actionType: INTERFACE, showAllItems: true },
	{ actionType: ServiceType.Integration, showAllItems: false },
	{ actionType: ServiceType.Ply, showAllItems: false },
	{ actionType: ServiceType.Internal, showAllItems: true },
];

export default {
	name: 'BlockEditorAppSelect',
	components: {
		ActionsMenu,
		AddNewStepHeader,
		AppSelectSubMenu,
	},
	computed: {
		...mapGetters([element.fullPath, nodesDataByUUID.fullPath, getServiceAllActions.fullPath]),
		...mapGettersShorthand({
			getAllServicesList,
			getAllActiveServicesList,
			getAllServiceActionCollections,
			getService,
			getServiceActionCollection,
			isAIMiniAppSupportOn,
			isLoomOn,
			editWorkflow,
			getPlacement,
			editNode,
			getServiceBySlug,
			getServiceActionCollectionBySlug,
			getServiceActionsByVersion,
			isEndRunNodePossible,
			useShortcutCommand,
		}),
		hasElement() {
			return !!this.element;
		},
		element() {
			return this[element.fullPath];
		},
		elementPlacement() {
			return this.getPlacement(this.element?.placement);
		},
		elementService() {
			return this.getService(this.elementPlacement?.service);
		},
		elementServiceSlug() {
			return this.elementService?.service_slug;
		},
		isFirstAppSuggestionBecauseOfElementPreset() {
			return this.hasElement && !!this.elementService;
		},
		doesContextualNodeHaveContextualActions() {
			if (this.element) {
				return AppActionService.doesElementHaveContextualAction(this.allElementActions, this.element);
			}
			return false;
		},
		allElementActions() {
			return this[getServiceAllActions.fullPath](this.contextualNodeServiceId).actions;
		},
		contextualNodeServiceId() {
			return this.elementService?.id;
		},
		mostUsedServicesSlugs() {
			const cnts = this.allNodesServiceSlugs.reduce((obj, val) => {
				obj[val] = (obj[val] || 0) + 1;
				return obj;
			}, {});
			const sortedSlugsByCount = Object.keys(cnts).sort((a, b) => cnts[b] - cnts[a]);
			// if its an add-on, that starts with a button, make it the first choice
			if (this.isFirstAppSuggestionBecauseOfElementPreset) {
				sortedSlugsByCount.unshift(this.elementServiceSlug);
			}
			return sortedSlugsByCount;
		},
		allNodesServiceSlugs() {
			const allNodesDataAsArray = Object.values(this.allNodesData || {});
			return allNodesDataAsArray.map(({ service_slug }) => service_slug);
		},
		allNodesData() {
			return this[nodesDataByUUID.fullPath];
		},

		allMenuItemsOnDisplay() {
			if (this.showMiniApps) {
				return this.miniApps;
			}
			const allItems = [];
			ACTION_TYPES_LIST.map((action) => {
				if (!this.showExpandedMenu || this.showExpandedMenu === action.actionType) {
					const subMenuItems = this.filterItems(this.appTypesItems[action.actionType].apps);
					const numOfAppsToDisplay = this.shouldShowAllItems(action) ? subMenuItems.length : this.getNumOfElementsToDisplayInPreview();
					const previewedSubMenuItems = subMenuItems.slice(0, numOfAppsToDisplay);
					previewedSubMenuItems.map((subItem) => {
						allItems.push({ ...subItem, uuid: AppService.getUniqueId(action.actionType, subItem.id, subItem.service_name) });
					});
				}
			});
			if (this.searchString && this.searchString.length > 2 && !this.showExpandedMenu) {
				const allActions = this.actionsFromSearchWithoutUnChangeableActions;
				allActions.map((subItem) => {
					allItems.push({ ...subItem, uuid: AppService.getUniqueId('action', subItem.id, subItem.service_name) });
				});
			}
			return allItems;
		},
		miniApps() {
			const { widgetApp, isAIMiniAppSupportOn, isLoomOn } = this;
			return BlockEditorAppSelectService.getMiniAppsMenuItems(widgetApp.id, isAIMiniAppSupportOn, isLoomOn);
		},
		widgetApp() {
			const apps = this.getAllServicesList;
			const widgetApp = apps.find((app) => AppService.isSlugOfWidget(app.service_slug));
			return widgetApp;
		},
		appTypesItems() {
			const apps = [...this.getAllActiveServicesList, ...this.getAllServiceActionCollections.filter((sac) => sac.actions.length > 0)]
				.filter((action) => WorkflowService.isActionAllowedInWorkflow(action.service_slug, this.editWorkflow.is_notification))
				.sort(BlockEditorAppSelectService.sortByName)
				.sort((...args) => BlockEditorAppSelectService.compareFromListBySlug(POPULAR_APPS_SLUGS, ...args))
				.sort(BlockEditorAppSelectService.sortByOrder)
				.sort((...args) => BlockEditorAppSelectService.compareFromListBySlug(this.mostUsedServicesSlugs, ...args));

			return BlockEditorAppSelectService.getAppsItems(apps, this['selectedWorkflow/element'], this.widgetApp?.id);
		},
		actionsFromSearchWithoutUnChangeableActions() {
			if (this.isChangingService) {
				return this.actionsFromSearch.filter((action) => !UN_CHANGEABLE_TO_AND_FROM_SERVICE_SLUGS.includes(action.service_slug));
			} else {
				return this.actionsFromSearch;
			}
		},
		isChangingService() {
			return !!this.editNode?.service;
		},
	},
	methods: {
		...mapActionsShorthand({ removeTempBlock, clearEditNode, setParamInEditNode, getActionsOfApp, setShortcutCommand }),
		sortActionsByBrowserActions(actions) {
			const sorted = actions.sort((a, b) => {
				if (a.browserAction && b.browserAction) {
					return 1;
				}
				if (a.browserAction) {
					return 1;
				}
				return -1;
			});
			return sorted;
		},
		scrollToElement(elementTop) {
			const el = this.$refs.editor_container;
			el.scrollTop += elementTop;
		},
		hideFullMenu() {
			if (this.showMiniApps) {
				this.showMiniApps = false;
				return;
			}
			this.showExpandedMenu = false;
		},
		shouldShowAllItems(action) {
			if (this.searchString && this.searchString.length > 0) {
				return true;
			}
			if (this.showExpandedMenu === action.actionType) {
				return true;
			} else {
				return action.showAllItems;
			}
		},
		onActionSelect(action) {
			this.selectApp({ id: action.service_id });
		},
		async onFailure(err) {
			this.$toast.error(ErrorHandlingService.extractErrorMessage(err)); // Picking the service from block-editor
			if (err instanceof NodeCreationError) {
				await this.removeTempBlock();
				await this.clearEditNode();
			} else if (!(err instanceof NodeUpdateError)) {
				console.error(err);
			}
		},
		selectApp(app) {
			if (app.isMiniApp) {
				this.showMiniApps = true;
				return;
			}
			if (app.miniAppType) {
				this.handleMiniAppCreation(app);
			} else if (app.isServiceActionCollection) {
				this.$emit('action-collection-selected', app.id);
			} else {
				this.setParamInEditNode({ key: 'service', value: app.id, onFailure: this.onFailure });
				this.$emit('app-selected');
			}
		},
		async getActionOfApp(app, actionKey) {
			/**
			 * Fetch if needed apps actions and return a specific action by key if exists
			 */
			await this.getActionsOfApp({ id: app.id, version: app.version });
			const allServiceActions = this[getServiceAllActions.fullPath](app.id);
			return allServiceActions.actions.find((action) => action.key === actionKey);
		},
		async onCreateCodeHelperClicked() {
			// Load the run javascript action
			await this.selectAppAndAction(ServiceSlugs.CodeBlock, runJsActionKey);
		},
		handleMiniAppCreation(app) {
			this.setParamInEditNode({ key: 'action_slug', value: app.action_slug });
			this.setParamInEditNode({ key: 'params_map', value: { mini_app_type: app.miniAppType } });
			this.setParamInEditNode({ key: 'service', value: app.id }); // important to keep it last, because after service is changed we reload editNode
			this.$emit('app-selected');
		},
		onSeeAll(actionType) {
			this.showExpandedMenu = actionType;
		},
		filterItems(allItems) {
			const lowerSearchString = this.searchString ? this.searchString.toLowerCase() : '';
			const res = allItems
				.filter((item) => item.service_name.toLowerCase().includes(lowerSearchString))
				.filter((item) => item.service_slug !== ServiceSlugs.EndRun || (this.editNode?.id && this.isEndRunNodePossible(this.editNode.id)));
			if (this.isChangingService) {
				return res.filter((item) => !UN_CHANGEABLE_TO_AND_FROM_SERVICE_SLUGS.includes(item.service_slug));
			} else {
				return res;
			}
		},
		isNoResults(allFilteredItems) {
			const doesHaveActions = this.actionsFromSearchWithoutUnChangeableActions.length > 0;
			return allFilteredItems.length === 0 && !doesHaveActions && !this.isLoadingActions;
		},
		populateActionWithIntegrationDetails(action) {
			const app = AppActionService.doesActionHasServiceActionCollection(action)
				? this.getServiceActionCollection(AppActionService.getActionServiceActionCollection(action))
				: this.getService(action.service_id);
			return ActionsMenuService.populateActionWithIntegrationDetails(action, app);
		},
		async getActions() {
			if (this.searchString && this.searchString.length < 3) {
				this.actionsFromSearch = [];
				return [];
			}
			this.isLoadingActions = true;
			const actions = await api.searchServiceActions(this.searchString);
			const sortedActions = this.sortActionsByBrowserActions(actions);
			this.isLoadingActions = false;
			this.actionsFromSearch = sortedActions.map(this.populateActionWithIntegrationDetails);
		},
		getNumOfElementsToDisplayInPreview() {
			let numOfAppsToDisplay = MAX_NUM_OF_APPS_TO_DISPLAY_IN_PREVIEW;
			const windowHeight = window.innerHeight;
			if (windowHeight < 1000) {
				numOfAppsToDisplay = 5;
			}
			if (windowHeight < 850) {
				numOfAppsToDisplay = 4;
			}
			if (windowHeight < 700) {
				numOfAppsToDisplay = 3;
			}
			return numOfAppsToDisplay;
		},
		onArrowDown() {
			const allItemsOnDisplay = this.allMenuItemsOnDisplay;
			const currentFocusedItemIndex = allItemsOnDisplay.findIndex((it) => it.uuid === this.focusedItemUUID);
			const newFocusedItemIndex = currentFocusedItemIndex + 1 >= allItemsOnDisplay.length ? 0 : currentFocusedItemIndex + 1;
			const newFocusedItem = allItemsOnDisplay[newFocusedItemIndex];
			this.focusedItemUUID = newFocusedItem?.uuid;
		},
		onArrowUp() {
			const allItemsOnDisplay = this.allMenuItemsOnDisplay;
			const currentFocusedItemIndex = allItemsOnDisplay.findIndex((it) => it.uuid === this.focusedItemUUID);
			const newFocusedItemIndex = currentFocusedItemIndex - 1 < 0 ? allItemsOnDisplay.length - 1 : currentFocusedItemIndex - 1;
			const newFocusedItem = allItemsOnDisplay[newFocusedItemIndex];
			this.focusedItemUUID = newFocusedItem?.uuid;
		},
		async handleShortcutCommand() {
			const handlers = {
				[CreateNodeShortcutCommand.EndRun]: () => this.selectApp(this.getServiceBySlug(ServiceSlugs.EndRun)),
				[CreateNodeShortcutCommand.Interface]: () => this.selectApp(this.getServiceBySlug(ServiceSlugs.Widgets)),
				[CreateNodeShortcutCommand.MiniApp]: () => this.selectApp({ isMiniApp: true }),
				[CreateNodeShortcutCommand.Integration]: () => this.onSeeAll(ServiceType.Integration),
				[CreateNodeShortcutCommand.Formula]: () => this.selectApp(this.getServiceActionCollectionBySlug('formulas')),
				[CreateNodeShortcutCommand.Branch]: () => this.selectApp(this.getServiceBySlug(ServiceSlugs.Branches)),
				[CreateNodeShortcutCommand.AI]: () => this.selectApp(this.getServiceActionCollectionBySlug('ai')),
				[CreateNodeShortcutCommand.SendAnEmail]: () => this.selectAppAndAction(ServiceSlugs.TransactionalEmails, 'send_email'),
			};
			await Promise.all([handlers[this.useShortcutCommand]?.(), this.setShortcutCommand({})]);
		},
		async selectAppAndAction(serviceSlug, actionKey) {
			const app = this.getServiceBySlug(serviceSlug);
			const action = await this.getActionOfApp(app, actionKey);
			this.selectApp(app);
			if (action) {
				const newAction = { service_id: app.id, id: action.id };
				this.$emit('action-selected', newAction);
				this.$emit('ply-action-selected');
			}
		},
	},
	data: () => ({
		searchString: '',
		ACTION_TYPES_LIST,
		showMiniApps: false,
		actionsFromSearch: [],
		actionShowMore: false,
		focusedItemUUID: null,
		helpersShowMore: false,
		showExpandedMenu: false,
		isLoadingActions: false,
	}),
	watch: {
		searchString: {
			handler: debounce(async function (newV) {
				if (newV && !this.showExpandedMenu) {
					await this.getActions();
				}
			}, 300),
			immediate: true,
		},
	},
	mounted() {
		if (this.useShortcutCommand) {
			this.handleShortcutCommand();
		}
	},
};
</script>

<style scoped lang="scss">
.no-results {
	font-size: 14px;
}
.editor-container {
	overflow-y: auto;
	overflow-x: hidden;
	background: var(--bg-color);
}
</style>
